import React, { useState } from 'react';
import PropTypes, { any } from 'prop-types';
import { ThemeProvider } from 'styled-components';
import {
  TAGGED_CONTENT_TILE_THEMES,
  getTaggedContentTileTheme,
} from 'utils/get-theme-selector';
import { Headline, Paragraph } from 'components/atoms';
import { black, white } from 'styles/colors';
import * as Styled from './TaggedContentTile.styled';
import AudioPlayer from '../audio-player/AudioPlayer';

/**
 * Renders a content tile
 * @param {Object} tile tile object
 * @param {Function} handleClick click handler
 * @param {string|null} className The component class name.
 * @returns {ReactElement}
 */
/* eslint-disable no-unused-vars */
const TaggedContentTile = ({ tile, handleClick, className }) => {
  const {
    title,
    description,
    image,
    backgroundColor,
    textColor,
    eyebrow,
    themeSelector,
    cta,
    audio,
  } = tile;
  const themeType = getTaggedContentTileTheme(themeSelector?.toLowerCase());
  const statTexts = description?.split('__');
  const [isFlipped, setIsFlipped] = useState(false);

  const getTileByType = () => {
    switch (themeType) {
      case TAGGED_CONTENT_TILE_THEMES.VIDEO_2X1:
        return (
          <Styled.TaggedContentTile className={className} onClick={handleClick}>
            <Styled.LeftContainer>
              <Styled.TopContainer>
                {eyebrow && <Styled.Eyebrow>{eyebrow}</Styled.Eyebrow>}
              </Styled.TopContainer>
              <Styled.BottomContainer>
                <Headline
                  tag="h3"
                  styling="h3"
                  text={title}
                  className="tile-title"
                />
                <Paragraph text={description} />
                <Styled.ButtonCta
                  type="roundRightArrow"
                  className="button-arrow"
                />
              </Styled.BottomContainer>
            </Styled.LeftContainer>
            <Styled.RightContainer>
              <Styled.ImageContainer data-src={image?.url} />
            </Styled.RightContainer>
          </Styled.TaggedContentTile>
        );
      case TAGGED_CONTENT_TILE_THEMES.AUDIO_FLIP:
        return (
          <Styled.FlipContainer onClick={() => setIsFlipped(!isFlipped)}>
            <Styled.FlippableTaggedContentTile className={className}>
              <Styled.FlipFront>
                <Styled.TopContainer data-src={image?.url}>
                  {eyebrow && <Styled.Eyebrow>{eyebrow}</Styled.Eyebrow>}
                </Styled.TopContainer>
                <Styled.BottomContainer>
                  <Headline
                    tag="h3"
                    styling="h3"
                    text={title}
                    className="tile-title"
                  />
                  <Styled.ButtonCta
                    type="roundUndoArrow"
                    className="button-arrow"
                  />
                </Styled.BottomContainer>
              </Styled.FlipFront>
              <Styled.FlipBack>
                <Styled.TopContainer>
                  {eyebrow && <Styled.Eyebrow>{eyebrow}</Styled.Eyebrow>}
                </Styled.TopContainer>
                <Styled.BottomContainer>
                  {audio && (
                    <AudioPlayer
                      title={audio.title}
                      caption={audio.caption}
                      data={audio.audio}
                      titleColor={textColor || white}
                      modifier="contentTile"
                      isDarkTheme
                    />
                  )}
                  <Styled.ButtonCta
                    type="roundUndoArrow"
                    className="button-arrow"
                    onClick={() => setIsFlipped(!isFlipped)}
                  />
                </Styled.BottomContainer>
              </Styled.FlipBack>
            </Styled.FlippableTaggedContentTile>
          </Styled.FlipContainer>
        );
      case TAGGED_CONTENT_TILE_THEMES.STAT:
        return (
          <Styled.TaggedContentTile
            data-src={image?.url}
            className={className}
            onClick={handleClick}
          >
            <Styled.TopContainer data-src={image?.url}>
              {eyebrow && <Styled.Eyebrow>{eyebrow}</Styled.Eyebrow>}
              {statTexts && statTexts.length > 1 && (
                <Styled.StatTitle
                  styling="stat"
                  className="bold-stat"
                  text={statTexts[1]}
                />
              )}
              {statTexts && statTexts.length > 2 && (
                <Paragraph text={statTexts[2]} />
              )}
            </Styled.TopContainer>
            <Styled.BottomContainer>
              <Headline
                tag="h3"
                styling="h3"
                text={title}
                className="tile-title"
              />
              <Styled.ButtonCta
                type="roundRightArrow"
                className="button-arrow"
              />
            </Styled.BottomContainer>
          </Styled.TaggedContentTile>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider
      theme={{
        backgroundColor: backgroundColor || black,
        textColor: textColor || white,
        hasImage: image,
        wide: themeType === TAGGED_CONTENT_TILE_THEMES.VIDEO_2X1,
        isFlipped,
      }}
    >
      {getTileByType()}
    </ThemeProvider>
  );
};

TaggedContentTile.propTypes = {
  /**
   * The tile component
   */
  tile: PropTypes.objectOf(any),
  /**
   * click tile handler
   */
  handleClick: PropTypes.func,
  /**
   * Default className prop
   */
  className: PropTypes.string,
};

export default TaggedContentTile;
